import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateLocationThresholdSuccess } from 'commons/src/actions/LocationActions';
import SpinnerBlocker from 'commons/src/components/modals/ModalSpinnerBlocker';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { ThresholdUpdatePayload } from 'commons/src/models/customThresholdModels';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import { deleteSpaceThresholdSuccess } from '../../../actions/spaceActions';
import { useGetBuildingThresholdsQuery } from '../../../api';
import CustomThresholds from '../../account/thresholds/CustomThresholdSection';
import ThresholdMgmt from '../../account/thresholds/ThresholdMgmt';
import styles from './BuildingThresholds.module.scss';
import { makeCustomThresholdSelector } from './BuildingThresholdSelector';

type Props = {
    locationId: string;
};
const BuildingThresholds = ({ locationId }: Props): JSX.Element => {
    const customThresholds = useSelector(makeCustomThresholdSelector(locationId));
    const dispatch = useDispatch();
    const {
        data = {},
        isLoading,
        error,
    } = useGetBuildingThresholdsQuery(locationId, {
        refetchOnMountOrArgChange: true,
        skip: !locationId,
    });
    const fetchThresholdsError = error ? toErrorType(error) : undefined;

    if (fetchThresholdsError) {
        return <ResponseBox text={`ErrorCodes.${fetchThresholdsError.error}`} />;
    }
    const onDeleteCustomThresholdSuccess = (itemId: string): void => {
        dispatch(deleteSpaceThresholdSuccess(locationId, itemId));
    };

    const updateSuccessAction = (id: string, payload: ThresholdUpdatePayload): void => {
        dispatch(updateLocationThresholdSuccess(id, payload));
    };

    return (
        <SpinnerBlocker isLoading={isLoading}>
            <div>
                {customThresholds.length > 0 && (
                    <div className={styles.customThresholds}>
                        <CustomThresholds
                            deleteSuccess={onDeleteCustomThresholdSuccess}
                            parentLocationId={locationId}
                            childResourceScope={ThresholdsResourceScope.SPACE}
                            headerTextKey="CustomThresholds.SpacesWithCustomThresholds"
                            customThresholds={customThresholds}
                        />
                    </div>
                )}
                <ThresholdMgmt
                    id={locationId}
                    thresholdScope={ThresholdsResourceScope.LOCATION}
                    parentThresholdScope={ThresholdsResourceScope.USER_GROUP}
                    thresholdData={data}
                    buttonText="ThresholdSettings.UpdateThresholds"
                    parentUrl="/settings/thresholds"
                    updateSuccessAction={updateSuccessAction}
                />
            </div>
        </SpinnerBlocker>
    );
};

export default BuildingThresholds;
